import { JobType } from '../types/availability';

/**
 * Hour slots encoding utilities for frontend
 * Encodes/decodes 12-character strings representing availability from 7am-7pm
 * 
 * Format: Each character represents one hour
 * N = Not available
 * V = Available for virtual jobs only
 * P = Available for in-person jobs only  
 * B = Available for both virtual and in-person jobs
 * 
 * Position 0 = 7am, Position 1 = 8am, ..., Position 11 = 6pm
 */

export class HourSlotsEncoder {
  /**
   * Convert hour slots object to 12-character string
   */
  static encode(hourSlots: Record<number, JobType[]>): string {
    let result = 'NNNNNNNNNNNN';
    
    for (let hour = 7; hour <= 18; hour++) { // 7am to 6pm
      const slotIndex = hour - 7; // 0-11
      const jobTypes = hourSlots[hour] || [];
      
      let char = 'N';
      if (jobTypes.includes('virtual') && jobTypes.includes('in-person')) {
        char = 'B'; // Both
      } else if (jobTypes.includes('in-person')) {
        char = 'P'; // In-person only
      } else if (jobTypes.includes('virtual')) {
        char = 'V'; // Virtual only
      }
      
      result = result.substring(0, slotIndex) + char + result.substring(slotIndex + 1);
    }
    
    return result;
  }
  
  /**
   * Convert 12-character string to hour slots object
   */
  static decode(hourSlots: string): Record<number, JobType[]> {
    const result: Record<number, JobType[]> = {};
    
    for (let i = 0; i < Math.min(12, hourSlots.length); i++) {
      const hour = i + 7; // 7am to 6pm
      const char = hourSlots[i];
      
      switch (char) {
        case 'V':
          result[hour] = ['virtual'];
          break;
        case 'P':
          result[hour] = ['in-person'];
          break;
        case 'B':
          result[hour] = ['virtual', 'in-person'];
          break;
        case 'N':
        default:
          // Not available - don't add to result
          break;
      }
    }
    
    return result;
  }
  
  /**
   * Get combined job types from hour slots string
   */
  static getJobTypes(hourSlots: string): JobType[] {
    const jobTypesSet = new Set<JobType>();
    
    for (const char of hourSlots) {
      switch (char) {
        case 'V':
          jobTypesSet.add('virtual');
          break;
        case 'P':
          jobTypesSet.add('in-person');
          break;
        case 'B':
          jobTypesSet.add('virtual');
          jobTypesSet.add('in-person');
          break;
      }
    }
    
    return Array.from(jobTypesSet);
  }
  
  /**
   * Get start and end times from hour slots string
   */
  static getTimeRange(hourSlots: string): { startTime?: string; endTime?: string } {
    let firstAvailableHour = -1;
    let lastAvailableHour = -1;
    
    for (let i = 0; i < Math.min(12, hourSlots.length); i++) {
      if (hourSlots[i] !== 'N') {
        if (firstAvailableHour === -1) {
          firstAvailableHour = i;
        }
        lastAvailableHour = i;
      }
    }
    
    if (firstAvailableHour === -1) {
      return {}; // No availability
    }
    
    const startHour = firstAvailableHour + 7;
    const endHour = lastAvailableHour + 8; // End time is one hour after the last slot
    
    return {
      startTime: `${startHour.toString().padStart(2, '0')}:00`,
      endTime: `${endHour.toString().padStart(2, '0')}:00`
    };
  }

  /**
   * Check if hour slots string represents any availability
   */
  static hasAvailability(hourSlots: string): boolean {
    return hourSlots.includes('V') || hourSlots.includes('P') || hourSlots.includes('B');
  }

  /**
   * Get a human-readable description of the availability
   */
  static getDescription(hourSlots: string): string {
    if (!this.hasAvailability(hourSlots)) {
      return 'Not available';
    }

    const timeRange = this.getTimeRange(hourSlots);
    const jobTypes = this.getJobTypes(hourSlots);
    
    const timeStr = timeRange.startTime && timeRange.endTime 
      ? `${this.formatTime(timeRange.startTime)} - ${this.formatTime(timeRange.endTime)}`
      : 'Multiple time blocks';
      
    const jobTypesStr = jobTypes.includes('virtual') && jobTypes.includes('in-person') 
      ? 'Virtual & In-person'
      : jobTypes.includes('in-person')
        ? 'In-person only'
        : 'Virtual only';
        
    return `${timeStr} (${jobTypesStr})`;
  }
  
  private static formatTime(time: string): string {
    const [hours] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${ampm}`;
  }
  
  /**
   * Get contiguous time blocks with job types
   */
  static getTimeBlocks(hourSlots: string): Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> {
    const blocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> = [];
    
    let currentBlockStart = -1;
    let currentBlockTypes: string | null = null;
    
    for (let i = 0; i < Math.min(12, hourSlots.length); i++) {
      const char = hourSlots[i];
      
      if (char === 'N') {
        // End current block if exists
        if (currentBlockStart !== -1 && currentBlockTypes !== null) {
          const jobTypes = this.charToJobTypes(currentBlockTypes);
          blocks.push({
            startTime: `${(currentBlockStart + 7).toString().padStart(2, '0')}:00`,
            endTime: `${(i + 7).toString().padStart(2, '0')}:00`,
            jobTypes
          });
        }
        currentBlockStart = -1;
        currentBlockTypes = null;
      } else {
        // Check if this is a new block or continuation
        if (currentBlockTypes !== char) {
          // End previous block if exists
          if (currentBlockStart !== -1 && currentBlockTypes !== null) {
            const jobTypes = this.charToJobTypes(currentBlockTypes);
            blocks.push({
              startTime: `${(currentBlockStart + 7).toString().padStart(2, '0')}:00`,
              endTime: `${(i + 7).toString().padStart(2, '0')}:00`,
              jobTypes
            });
          }
          // Start new block
          currentBlockStart = i;
          currentBlockTypes = char;
        }
      }
    }
    
    // Handle last block if exists
    if (currentBlockStart !== -1 && currentBlockTypes !== null) {
      const jobTypes = this.charToJobTypes(currentBlockTypes);
      blocks.push({
        startTime: `${(currentBlockStart + 7).toString().padStart(2, '0')}:00`,
        endTime: `${(12 + 7).toString().padStart(2, '0')}:00`, // 7pm (end of day)
        jobTypes
      });
    }
    
    return blocks;
  }
  
  /**
   * Convert character to job types array
   */
  private static charToJobTypes(char: string): JobType[] {
    switch (char) {
      case 'V':
        return ['virtual'];
      case 'P':
        return ['in-person'];
      case 'B':
        return ['virtual', 'in-person'];
      default:
        return [];
    }
  }
  
  /**
   * Format time blocks as a readable string
   */
  static formatTimeBlocks(blocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }>): string {
    if (blocks.length === 0) return '';
    
    return blocks.map(block => {
      const startTime = this.formatTimeNoAmPm(block.startTime);
      const endTime = this.formatTimeNoAmPm(block.endTime);
      
      let jobTypeIndicator = '';
      const hasVirtual = block.jobTypes.includes('virtual');
      const hasInPerson = block.jobTypes.includes('in-person');
      
      if (hasVirtual && hasInPerson) {
        jobTypeIndicator = ''; // 'V-IP' can be omitted for brevity
      } else if (hasVirtual) {
        jobTypeIndicator = 'V';
      } else if (hasInPerson) {
        jobTypeIndicator = 'IP';
      }
      
      return `${startTime}-${endTime} ${jobTypeIndicator}`;
    }).join(', ');
  }
  
  /**
   * Format time to 12-hour format without AM/PM
   */
  private static formatTimeNoAmPm(timeStr: string): string {
    if (!timeStr) return '';
    
    const [hours] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    
    // Convert to 12-hour format without AM/PM (context is clear)
    if (hour === 0) return '12'; // midnight
    else if (hour > 12) return (hour - 12).toString(); // PM hours
    else return hour.toString();
  }
}